import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from "firebase/firestore";

const formatCurrency = (num) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(num || 0);
};

const formatCompact = (num) => {
    return new Intl.NumberFormat('en-US', {
        notation: "compact",
        maximumFractionDigits: 1
    }).format(num || 0);
};

const Home = () => {
  const [loading, setLoading] = useState(true);
  
  // --- Filters for each section ---
  const currentYear = new Date().getFullYear().toString();
  
  // 1. Profit Filter
  const [profitYear, setProfitYear] = useState(currentYear);
  const [profitMonth, setProfitMonth] = useState('All');

  // 2. Received Filter
  const [receivedYear, setReceivedYear] = useState(currentYear);
  const [receivedMonth, setReceivedMonth] = useState('All');

  // 3. Discount Filter (New)
  const [discountYear, setDiscountYear] = useState(currentYear);
  const [discountMonth, setDiscountMonth] = useState('All');

  // --- Graph Filter ---
  const now = new Date();
  const [graphYear, setGraphYear] = useState(now.getFullYear().toString());
  const [graphMonth, setGraphMonth] = useState('All'); 

  // Summary State
  const [summary, setSummary] = useState({
    yearlySales: 0,
    yearlyProfit: 0,
    totalShipmentsCount: 0,
    currentMonthBill: 0,
    currentMonthShipmentsCount: 0,
    lastMonthProfit: 0,
    lastMonthShipmentsCount: 0,
    lastMonthName: '',
    totalDue: 0,
    totalReceived: 0
  });

  const [chartData, setChartData] = useState([]);
  
  // Raw Data States
  const [allInvoices, setAllInvoices] = useState([]);
  const [allPayments, setAllPayments] = useState([]); 

  // Processed List States
  const [clientProfits, setClientProfits] = useState([]); 
  const [clientDues, setClientDues] = useState([]); 
  const [clientReceived, setClientReceived] = useState([]);
  const [clientDiscounts, setClientDiscounts] = useState([]); 

  // Helpers
  const months = [
    { value: "01", name: "January" }, { value: "02", name: "February" }, { value: "03", name: "March" },
    { value: "04", name: "April" }, { value: "05", name: "May" }, { value: "06", name: "June" },
    { value: "07", name: "July" }, { value: "08", name: "August" }, { value: "09", name: "September" },
    { value: "10", name: "October" }, { value: "11", name: "November" }, { value: "12", name: "December" }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchData = async () => {
      try {
        const invoicesSnap = await getDocs(collection(db, "invoices"));
        const invoices = invoicesSnap.docs.map(doc => doc.data());
        
        const paymentsSnap = await getDocs(collection(db, "payments"));
        const payments = paymentsSnap.docs.map(doc => doc.data());

        setAllInvoices(invoices);
        setAllPayments(payments); 
        
        processDashboardData(invoices, payments);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- Effects to update lists when filters change ---

  // 1. Profit List Update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (allInvoices.length > 0) {
      updateClientProfitList(allInvoices, profitYear, profitMonth);
    }
  }, [profitYear, profitMonth, allInvoices]);

  // 2. Received List Update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (allPayments.length > 0) {
      updateClientReceivedList(allPayments, receivedYear, receivedMonth);
    }
  }, [receivedYear, receivedMonth, allPayments]);

  // 3. Discount List Update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (allInvoices.length > 0) {
      updateClientDiscountList(allInvoices, discountYear, discountMonth);
    }
  }, [discountYear, discountMonth, allInvoices]);

  // 4. Graph Update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (allInvoices.length > 0) {
      updateGraphData(allInvoices, graphYear, graphMonth);
    }
  }, [graphYear, graphMonth, allInvoices]);


  // --- Data Processing Functions ---

  const processDashboardData = (invoices, payments) => {
    const now = new Date();
    const currentYearString = now.getFullYear().toString();
    const currentYearMonth = now.toISOString().slice(0, 7); 
    
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthYearMonth = lastMonthDate.toISOString().slice(0, 7);
    const lastMonthNameStr = lastMonthDate.toLocaleString('default', { month: 'long' });

    let yearlySales = 0, yearlyProfit = 0, allTimeShipments = 0;
    let currBill = 0, currShipments = 0, lastMonthProfit = 0;
    let lastMonthShipments = 0;
    
    // Track latest invoice for due calculation
    const clientLatestInvoices = {}; 

    // Invoice Processing
    invoices.forEach(inv => {
      const invTotal = parseFloat(inv.totalBdt || 0);
      const invItems = (inv.items && Array.isArray(inv.items)) ? inv.items : [];
      
      let invProfit = 0;
      invItems.forEach(item => {
         invProfit += parseFloat(item.profit || 0);
      });

      if (inv.invoiceYearMonth && inv.invoiceYearMonth.startsWith(currentYearString)) {
          yearlySales += invTotal;
          yearlyProfit += invProfit;
      }
      allTimeShipments += invItems.length;

      if (inv.invoiceYearMonth === currentYearMonth) {
        currBill += invTotal;
        currShipments += invItems.length;
      }
      if (inv.invoiceYearMonth === lastMonthYearMonth) {
        lastMonthProfit += invProfit;
        lastMonthShipments += invItems.length;
      }

      // NEW DUE LOGIC: Find the latest invoice based on year-month and save its finalTotalBdt
      const cName = inv.companyName || "Unknown";
      const ym = inv.invoiceYearMonth || "0000-00";
      
      if (!clientLatestInvoices[cName]) {
          clientLatestInvoices[cName] = { 
              name: cName, 
              latestYM: ym, 
              finalDue: parseFloat(inv.finalTotalBdt || 0) 
          };
      } else {
          if (ym > clientLatestInvoices[cName].latestYM) {
              clientLatestInvoices[cName].latestYM = ym;
              clientLatestInvoices[cName].finalDue = parseFloat(inv.finalTotalBdt || 0);
          }
      }
    });

    // Payments Processing (All Time Summary)
    let totalReceivedAmount = 0;
    payments.forEach(pay => {
        const amount = parseFloat(pay.totalAmount || pay.amount || 0);
        totalReceivedAmount += amount;
    });

    // Calculate Global Due from the latest invoices
    let totalGlobalDue = 0;
    const clientDuesList = [];
    
    Object.values(clientLatestInvoices).forEach(c => {
        if (c.finalDue > 0) {
            totalGlobalDue += c.finalDue;
            clientDuesList.push({ name: c.name, totalDue: c.finalDue });
        }
    });

    setSummary({
      yearlySales, yearlyProfit, totalShipmentsCount: allTimeShipments,
      currentMonthBill: currBill, currentMonthShipmentsCount: currShipments,
      lastMonthProfit, lastMonthShipmentsCount: lastMonthShipments,
      lastMonthName: lastMonthNameStr,
      totalDue: totalGlobalDue,
      totalReceived: totalReceivedAmount
    });

    // Set Due List (Sorted highest due first)
    setClientDues(clientDuesList.sort((a, b) => b.totalDue - a.totalDue));
    
    // Initial Calls for filtered lists
    updateGraphData(invoices, graphYear, graphMonth);
    updateClientProfitList(invoices, profitYear, profitMonth);
    updateClientReceivedList(payments, receivedYear, receivedMonth);
    updateClientDiscountList(invoices, discountYear, discountMonth);
  };

  const updateGraphData = (invoices, year, month) => {
      let gSales = 0, gBooking = 0, gProfit = 0, gShipments = 0;
      let label = "";

      if (month === 'All') {
          label = `Yearly Overview (${year})`;
          invoices.forEach(inv => {
              if (inv.invoiceYearMonth && inv.invoiceYearMonth.startsWith(year)) {
                  const invTotal = parseFloat(inv.totalBdt || 0);
                  const invItems = (inv.items && Array.isArray(inv.items)) ? inv.items : [];
                  let invBooking = 0, invProfit = 0;
                  invItems.forEach(item => {
                      invBooking += parseFloat(item.bookingTotal || 0);
                      invProfit += parseFloat(item.profit || 0);
                  });
                  gSales += invTotal;
                  gBooking += invBooking;
                  gProfit += invProfit;
                  gShipments += invItems.length;
              }
          });
      } else {
          const targetYearMonth = `${year}-${month}`;
          const monthName = months.find(m => m.value === month)?.name || month;
          label = `${monthName} ${year}`;

          invoices.forEach(inv => {
              if (inv.invoiceYearMonth === targetYearMonth) {
                  const invTotal = parseFloat(inv.totalBdt || 0);
                  const invItems = (inv.items && Array.isArray(inv.items)) ? inv.items : [];
                  let invBooking = 0, invProfit = 0;
                  invItems.forEach(item => {
                      invBooking += parseFloat(item.bookingTotal || 0);
                      invProfit += parseFloat(item.profit || 0);
                  });
                  gSales += invTotal;
                  gBooking += invBooking;
                  gProfit += invProfit;
                  gShipments += invItems.length;
              }
          });
      }

      setChartData([{ 
          name: label, 
          sales: gSales, 
          booking: gBooking, 
          profit: gProfit,
          shipments: gShipments 
      }]);
  };

  const updateClientProfitList = (invoices, year, month) => {
    const clientMap = {};
    invoices.forEach(inv => {
      if (inv.invoiceYearMonth && inv.invoiceYearMonth.startsWith(year)) {
        const invMonth = inv.invoiceYearMonth.split('-')[1];
        if (month !== 'All' && invMonth !== month) return;
        const cName = inv.companyName || "Unknown";
        let invProfit = 0;
        (inv.items || []).forEach(item => { invProfit += parseFloat(item.profit || 0); });
        if (!clientMap[cName]) clientMap[cName] = { name: cName, profit: 0 };
        clientMap[cName].profit += invProfit;
      }
    });
    setClientProfits(Object.values(clientMap).sort((a, b) => b.profit - a.profit));
  };

  const updateClientReceivedList = (payments, year, month) => {
    const clientMap = {};
    payments.forEach(pay => {
        let payDate = null;
        if (pay.paymentTimestamp && pay.paymentTimestamp.toDate) {
            payDate = pay.paymentTimestamp.toDate();
        } else if (pay.date) {
            const parts = pay.date.split('/');
            if(parts.length === 3) payDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }

        if (!payDate || isNaN(payDate)) return;

        const payYear = payDate.getFullYear().toString();
        const payMonth = String(payDate.getMonth() + 1).padStart(2, '0');

        if (payYear !== year) return;
        if (month !== 'All' && payMonth !== month) return;

        const cName = pay.companyName || "Unknown";
        const amount = parseFloat(pay.totalAmount || pay.amount || 0);
        
        if (!clientMap[cName]) clientMap[cName] = { name: cName, received: 0 };
        clientMap[cName].received += amount;
    });
    setClientReceived(Object.values(clientMap).sort((a, b) => b.received - a.received));
  };

  const updateClientDiscountList = (invoices, year, month) => {
    const clientMap = {};
    invoices.forEach(inv => {
      if (inv.invoiceYearMonth && inv.invoiceYearMonth.startsWith(year)) {
        const invMonth = inv.invoiceYearMonth.split('-')[1];
        if (month !== 'All' && invMonth !== month) return;
        
        const cName = inv.companyName || "Unknown";
        const discount = parseFloat(inv.discountAmount || 0); 

        if(discount > 0) {
            if (!clientMap[cName]) clientMap[cName] = { name: cName, discount: 0 };
            clientMap[cName].discount += discount;
        }
      }
    });
    setClientDiscounts(Object.values(clientMap).sort((a, b) => b.discount - a.discount));
  };

  // Totals for Footers
  const totalListProfit = clientProfits.reduce((acc, curr) => acc + curr.profit, 0);
  const totalListReceived = clientReceived.reduce((acc, curr) => acc + curr.received, 0);
  const totalListDue = clientDues.reduce((acc, curr) => acc + curr.totalDue, 0);
  const totalListDiscount = clientDiscounts.reduce((acc, curr) => acc + curr.discount, 0);

  if (loading) return <div className="loading-spinner">Loading Dashboard...</div>;

  return (
    <div className="dashboard-home">
      <h2 className="dashboard-title">Dashboard Overview</h2>

      {/* Summary Cards */}
      <div className="summary-cards" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px'}}>
        <div className="card blue" style={{borderLeft: '5px solid #3498db'}}>
          <h3>Total Shipment (Yearly)</h3>
          <p className="amount">৳ {formatCurrency(summary.yearlySales)}</p>
          <span className="subtitle">Jan - Dec {new Date().getFullYear()}</span>
        </div>
        
        <div className="card purple" style={{borderLeft: '5px solid #9b59b6', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
          <h3>Total Received</h3>
          <p className="amount" style={{fontSize: '24px', fontWeight: 'bold', margin: '10px 0', color: '#8e44ad'}}>৳ {formatCurrency(summary.totalReceived)}</p>
          <span className="subtitle">All Time Collection</span>
        </div>

        <div className="card red" style={{borderLeft: '5px solid #e74c3c'}}>
          <h3>Total Market Due</h3>
          <p className="amount">৳ {formatCurrency(summary.totalDue)}</p>
          <span className="subtitle">Outstanding Balance</span>
        </div>
        
        <div className="card orange" style={{borderLeft: '5px solid #f39c12'}}>
          <h3 style={{margin: 0, marginBottom: '10px'}}>Total Profit (Yearly)</h3>
          <p className="amount">৳ {formatCurrency(summary.yearlyProfit)}</p>
          <span className="subtitle">Jan - Dec {new Date().getFullYear()}</span>
        </div>
      </div>

      {/* --- Chart Section --- */}
      <div className="dashboard-grid" style={{ gridTemplateColumns: '1fr', marginTop: '30px' }}>
        <div className="chart-section box-shadow">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom: '1px solid #eee', paddingBottom: '15px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <h3 style={{margin: 0}}>Performance Graph</h3>
                <input 
                    type="number"
                    value={graphYear}
                    onChange={(e) => setGraphYear(e.target.value)}
                    style={{width: '60px', padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', textAlign: 'center'}}
                />
                <select value={graphMonth} onChange={(e) => setGraphMonth(e.target.value)} style={{padding: '5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px', background: '#f9f9f9'}}>
                    <option value="All">All Months (This Year)</option>
                    {months.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                </select>
            </div>
            
            <div className="chart-legend" style={{display:'flex', gap:'20px', fontSize:'13px', fontWeight: '500'}}>
                <span style={{display:'flex', alignItems:'center'}}><span style={{width:'12px', height:'12px', background:'linear-gradient(180deg, #3498db, #2980b9)', borderRadius:'3px', display:'inline-block', marginRight:'6px'}}></span> Total Shipment</span>
                <span style={{display:'flex', alignItems:'center'}}><span style={{width:'12px', height:'12px', background:'linear-gradient(180deg, #e74c3c, #c0392b)', borderRadius:'3px', display:'inline-block', marginRight:'6px'}}></span> Booking</span>
                <span style={{display:'flex', alignItems:'center'}}><span style={{width:'12px', height:'12px', background:'linear-gradient(180deg, #2ecc71, #27ae60)', borderRadius:'3px', display:'inline-block', marginRight:'6px'}}></span> Profit</span>
            </div>
          </div>
          
          <div className="bar-chart">
            {chartData.map((data, index) => {
                const maxVal = Math.max(data.sales, data.booking, data.profit) || 1;
                const heightSales = Math.max((data.sales / maxVal) * 80, 10); 
                const heightBooking = Math.max((data.booking / maxVal) * 80, 10);
                const heightProfit = Math.max((data.profit / maxVal) * 80, 10); 

                return (
                    <div key={index} className="bar-column">
                        <div style={{
                            marginBottom: '10px', background: '#ecf0f1', padding: '5px 12px', borderRadius: '20px', 
                            fontSize: '13px', fontWeight: 'bold', color: '#2c3e50', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            display: 'inline-flex', alignItems: 'center', gap: '5px'
                        }}>
                            <span>📦</span><span>{data.shipments} Shipments</span>
                        </div>

                        <div className="bars-group" style={{marginTop: '10px'}}>
                            <div className="single-bar-wrapper">
                                <span className="bar-value blue-text">{formatCompact(data.sales)}</span>
                                <div className="bar sales-bar" style={{height: `${heightSales}%`}} title={`Total Shipment: ৳ ${formatCurrency(data.sales)}`}></div>
                            </div>
                            <div className="single-bar-wrapper">
                                <span className="bar-value red-text">{formatCompact(data.booking)}</span>
                                <div className="bar booking-bar" style={{height: `${heightBooking}%`}} title={`Booking: ৳ ${formatCurrency(data.booking)}`}></div>
                            </div>
                            <div className="single-bar-wrapper">
                                <span className="bar-value green-text">{formatCompact(data.profit)}</span>
                                <div className="bar profit-bar" style={{height: `${heightProfit}%`}} title={`Profit: ৳ ${formatCurrency(data.profit)}`}></div>
                            </div>
                        </div>
                        <div style={{marginTop: '10px', fontSize: '14px', fontWeight: 'bold', color: '#555'}}>{data.name}</div>
                    </div>
                )
            })}
          </div>
        </div>
      </div>

      <div className="dashboard-grid-bottom">
        
        {/* 1. Client Wise Profit */}
        <div className="top-customers box-shadow">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '5px'}}>
            <h3 style={{margin: 0}}>Client Wise Profit</h3>
            <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
              <input type="number" value={profitYear} onChange={(e) => setProfitYear(e.target.value)} style={{width: '60px', padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', textAlign: 'center'}} />
              <select value={profitMonth} onChange={(e) => setProfitMonth(e.target.value)} style={{padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc'}}>
                <option value="All">All Month</option>
                {months.map(m => <option key={m.value} value={m.value}>{m.name.substring(0,3)}</option>)}
              </select>
            </div>
          </div>
          <ul>
            {clientProfits.map((cust, idx) => (
              <li key={idx} style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee'}}>
                <span className="name" style={{fontWeight: '500'}}>{cust.name}</span>
                <span style={{color: '#27ae60', fontWeight: 'bold'}}>
                  ৳ {formatCurrency(cust.profit)}
                </span>
              </li>
            ))}
          </ul>
          <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #ddd', marginTop: '10px', fontWeight: 'bold', fontSize: '15px'}}>
              <span>TOTAL PROFIT</span>
              <span style={{color: '#27ae60'}}>
                ৳ {formatCurrency(totalListProfit)}
              </span>
          </div>
        </div>

        {/* 2. Client Wise Total Received */}
        <div className="notifications box-shadow">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '5px'}}>
              <h3 style={{margin: 0}}>Client Wise Received</h3>
              <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                <input type="number" value={receivedYear} onChange={(e) => setReceivedYear(e.target.value)} style={{width: '60px', padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', textAlign: 'center'}} />
                <select value={receivedMonth} onChange={(e) => setReceivedMonth(e.target.value)} style={{padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc'}}>
                  <option value="All">All Month</option>
                  {months.map(m => <option key={m.value} value={m.value}>{m.name.substring(0,3)}</option>)}
                </select>
              </div>
          </div>
          <ul style={{maxHeight: '300px', overflowY: 'auto'}}>
            {clientReceived.map((cust, idx) => (
              <li key={idx} style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee'}}>
                <span className="name" style={{fontWeight: '500'}}>{cust.name}</span>
                <span style={{color: '#8e44ad', fontWeight: 'bold'}}>৳ {formatCurrency(cust.received)}</span>
              </li>
            ))}
          </ul>
          <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #ddd', marginTop: '10px', fontWeight: 'bold', fontSize: '15px'}}>
              <span>TOTAL RECEIVED</span>
              <span style={{color: '#8e44ad'}}>৳ {formatCurrency(totalListReceived)}</span>
          </div>
        </div>

        {/* 3. Client Wise Total Due */}
        <div className="notifications box-shadow">
          <h3>Client Wise Total Due</h3>
          <ul>
            {clientDues.map((cust, idx) => (
              <li key={idx} style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee'}}>
                <span className="name" style={{fontWeight: '500'}}>{cust.name}</span>
                <span style={{color: '#e74c3c', fontWeight: 'bold'}}>৳ {formatCurrency(cust.totalDue)}</span>
              </li>
            ))}
          </ul>
          <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #ddd', marginTop: '10px', fontWeight: 'bold', fontSize: '15px'}}>
              <span>TOTAL DUE</span>
              <span style={{color: '#e74c3c'}}>৳ {formatCurrency(totalListDue)}</span>
          </div>
        </div>

        {/* 4. Client Wise Discount */}
        <div className="top-customers box-shadow">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '5px'}}>
            <h3 style={{margin: 0}}>Client Wise Discount</h3>
            <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
              <input type="number" value={discountYear} onChange={(e) => setDiscountYear(e.target.value)} style={{width: '60px', padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', textAlign: 'center'}} />
              <select value={discountMonth} onChange={(e) => setDiscountMonth(e.target.value)} style={{padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc'}}>
                <option value="All">All Month</option>
                {months.map(m => <option key={m.value} value={m.value}>{m.name.substring(0,3)}</option>)}
              </select>
            </div>
          </div>
          <ul>
            {clientDiscounts.length > 0 ? clientDiscounts.map((cust, idx) => (
              <li key={idx} style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee'}}>
                <span className="name" style={{fontWeight: '500'}}>{cust.name}</span>
                <span style={{color: '#d35400', fontWeight: 'bold'}}>৳ {formatCurrency(cust.discount)}</span>
              </li>
            )) : <li style={{padding: '20px', textAlign: 'center', color: '#999'}}>No discount data.</li>}
          </ul>
          <div style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #ddd', marginTop: '10px', fontWeight: 'bold', fontSize: '15px'}}>
              <span>TOTAL DISCOUNT</span>
              <span style={{color: '#d35400'}}>৳ {formatCurrency(totalListDiscount)}</span>
          </div>
        </div>

      </div>
    </div>
  )
}

export default Home;